<template>
  <TeamDropdownNavItemSkeleton class="mr-3 px-3 py-1" v-if="isLoading" />
  <section v-else class="is-clickable mr-3 p-3">
    <DropdownView
      position="is-bottom-right"
      class="is-narrow is-full-width"
      aria-role="list"
      @active-change="onActiveChange"
      :data-e2e="TeamCypressAttribute.inputs.TEAM_NAV_ITEM_DROPDOWN"
    >
      <template #trigger>
        <div class="is-flex is-flex-direction-row is-align-items-center">
          <div class="is-flex is-flex-direction-column is-flex-grow-1">
            <p class="has-text-weight-semibold has-text-grey is-size-5">
              {{ $t('team.current-team-title') }}
            </p>
            <p
              class="has-text-black has-text-weight-semibold is-uppercase is-size-5"
              :data-e2e="TeamCypressAttribute.views.TEAM_NAV_ITEM_CURRENT_TEAM"
            >
              {{ currentTeam.name }}
            </p>
          </div>
          <div class="is-narrow has-text-weight-bold">
            <i
              class="cls cls-caret-right has-transition-transform-ease"
              :class="{ expanded: isDropdownOpen }"
              :data-e2e="TeamCypressAttribute.views.TEAM_NAV_CARET_RIGHT_ICON"
            ></i>
          </div>
        </div>
      </template>
      <DropdownItemView
        v-for="team in teams"
        :key="team.id"
        @click="onTeamClicked(team)"
        :data-e2e="
          team.id
            ? `${TeamCypressAttribute.inputs.TEAM_NAV_ITEM_DROPDOWN_ITEM}-${team.id}`
            : TeamCypressAttribute.inputs.TEAM_NAV_ITEM_DROPDOWN_ITEM_PERSONAL_WORKSPACE
        "
      >
        <div class="content-box dropdown-option">
          <div class="columns">
            <div class="column">
              <p>{{ team.name }}</p>
            </div>
            <div
              v-if="isCurrentTeam(team)"
              class="column is-vcentered is-narrow m-t-2"
              :data-e2e="TeamCypressAttribute.views.TEAM_NAV_ITEM_CURRENT_TEAM_ICON"
            >
              <i class="cls cls-check has-text-primary" />
            </div>
          </div>
        </div>
      </DropdownItemView>
    </DropdownView>
  </section>
</template>

<script>
import { NavItem } from '@/modules/core/nav/models/NavItem';
import { TeamSessionService } from '@/modules/team/services/TeamSessionService';
import { TeamEvent } from '@/modules/team/enums/TeamEvent';
import { HasContainerMixin } from '@/modules/core/app/mixins/HasContainerMixin';
import DropdownView from '@/modules/core/ui/components/wrappers/DropdownView.vue';
import TeamSetTeamService from '@/modules/team/services/TeamSetTeamService';
import DropdownItemView from '@/modules/core/ui/components/wrappers/DropdownItemView.vue';
import TeamDropdownNavItemSkeleton from '@/modules/team/components/skeletons/TeamDropdownNavItemSkeleton.vue';
import { HasAuthUserMixin } from '/@/modules/core/app/mixins/HasAuthUserMixin';
import { TeamCypressAttribute } from '@/modules/team/constants/TeamCypressAttribute';
import { TeamPredefinedTeam } from '@/modules/team/constants/TeamPredefinedTeam';
import ModalService from '@/modules/core/ui/services/ModalService';
import ConfirmModal from '@/modules/core/ui/components/ConfirmModal.vue';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import ViewingEstateDetailsService from '@/modules/estate/services/ViewingEstateDetailsService';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'TeamDropdownNavItem',
  mixins: [HasContainerMixin, HasAuthUserMixin],
  components: {
    DropdownView,
    DropdownItemView,
    TeamDropdownNavItemSkeleton
  },
  props: {
    item: {
      type: NavItem,
      required: true
    }
  },
  static: {
    TeamCypressAttribute
  },
  events() {
    return {
      [TeamEvent.ON_TEAM_ADDED]: this.loadTeams,
      [TeamEvent.ON_TEAM_DELETED]: this.loadTeams
    };
  },
  data() {
    return {
      personalWorkspaceTeam: TeamPredefinedTeam.PERSONAL_WORKSPACE,
      isDropdownOpen: null,
      teams: null,
      service: this.$resolve(TeamSessionService),
      isLoading: false
    };
  },
  computed: {
    currentTeam() {
      if (!this.teams || this.teams.length === 0) {
        return this.personalWorkspaceTeam;
      }

      return this.teams.find((team) => this.isCurrentTeam(team));
    }
  },
  methods: {
    onActiveChange(value) {
      this.isDropdownOpen = value;
    },
    isCurrentTeam(team) {
      if (!this.authUser.currentTeam) {
        return this.personalWorkspaceTeam.id === team.id;
      }

      return this.authUser.currentTeam?.id === team.id;
    },
    async loadTeams() {
      this.isLoading = true;
      try {
        this.teams = await this.service.getEntities();
        this.teams.unshift(this.personalWorkspaceTeam);
      } finally {
        this.isLoading = false;
      }
    },
    async onTeamClicked(team) {
      this.isLoading = true;
      try {
        let currentTeam = team;
        if (this.personalWorkspaceTeam.id === team.id) {
          currentTeam = null;
        }

        if (this.authUser.isViewingEstateDetails) {
          return ModalService.show({
            parent: this,
            component: ConfirmModal,
            props: {
              title: this.$t('team.change-team-title'),
              subtitle: this.$t('team.change-team-subtitle'),
              primaryButtonText: this.$t('core.yes-continue'),
              secondaryButtonText: this.$t('core.no-cancel'),
              confirmFn: async () => {
                ViewingEstateDetailsService.unset();

                await TeamSetTeamService.setCurrentTeamQuietly(currentTeam);
                DocumentUIService.reloadAppAtRoot();
              }
            }
          });
        }

        await TeamSetTeamService.setCurrentTeam(currentTeam);
      } finally {
        this.isDropdownOpen = false;
        this.isLoading = false;
      }
    }
  },
  mounted() {
    this.loadTeams();
  }
};
</script>

<style scoped>
section {
  border-radius: var(--ce-0px) var(--ce-10px) var(--ce-10px) var(--ce-0px);
  border: var(--ce-2px) solid transparent;
  &:hover {
    border-color: var(--ce-primary);
    border-left-color: transparent;
    a {
      color: var(--ce-black) !important;
    }
  }
}

.cls-caret-right {
  color: var(--ce-primary);
  font-size: var(--ce-12px);

  &.expanded {
    transform: rotate(90deg);
  }
}

:deep(.dropdown .dropdown-item) {
  &:hover {
    background-color: var(--ce-primary-light);
    color: var(--ce-black);
  }
}

:deep(.dropdown .dropdown-menu) {
  padding: 0;
}

:deep(.dropdown .dropdown-trigger) {
  width: 100%;
}
</style>
